/**
 * Dev-only guard against the silent GBP currency fallback (SX-194).
 *
 * `formatDisplayValue` / `formatReviewValue` fall back to `DEFAULT_CURRENCY`
 * ('GBP') when a caller omits `formatOptions.currency`. That fallback is a
 * safety net, not a licence to skip threading the org currency - a currency
 * field rendered without the tenant's currency silently shows GBP even when
 * the tenant is on AUD/USD/etc.
 *
 * This helper emits a one-time console warning in development when a
 * currency-typed value is formatted with no explicit `currency`, so the
 * missing `{ locale, currency }` is caught at the call site during dev rather
 * than shipping as a wrong-currency display. It is a no-op in production and
 * never throws.
 */

const warned = new Set<string>()

export function warnMissingCurrency(source: string, fieldCode?: string): void {
  if (!import.meta.env.DEV) return
  const key = `${source}:${fieldCode ?? ''}`
  if (warned.has(key)) return
  warned.add(key)
  console.warn(
    `[formatting] ${source} formatted a currency field` +
      (fieldCode ? ` ("${fieldCode}")` : '') +
      ' without an explicit currency; falling back to the platform default ' +
      "(GBP). Pass formatOptions={{ locale, currency }} from useFormatters()/" +
      'useFormattingContext() so money follows the tenant currency (SX-194).',
  )
}
